import React, { useEffect, useState } from "react";
import "./InfoLateralResumoGeralComponent.scss";
import Chart from "react-google-charts";
import axios, { AxiosResponse } from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Accordion } from "react-bootstrap";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// Interfaces para os dados recebidos das APIs
interface CentroCustoValue {
  centrocusto: string;
  totalValorSaida: number;
}

interface Empresa {
  valorentrada: number;
  empresaselecionada: string;
  centrocusto: string;
  projetos: any[];
  valorsaida: number;
}

interface Saida {
  centrocusto: string;
  projetos: any;
  // Adicione outras propriedades conforme necessário
}

type CentroCusto = {
  centrocusto: string;
  totalValorSaida: number;
};

const InfoLateralResumoGeralComponent: React.FC = () => {
  // Estados para datas e valores financeiros
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [lucroBruto, setLucroBruto] = useState<number>(0);
  const [lucroLiquido, setLucroLiquido] = useState<number>(0);
  const [despesasFixas, setDespesasFixas] = useState<number>(0);
  const [despesasVariadas, setDespesasVariadas] = useState<number>(0);
  const [aPagar, setAPagar] = useState<number>(0);
  const [aReceber, setAReceber] = useState<number>(0);
  const [totalSalarios, setTotalSalarios] = useState<number>(0);

  // Estado para armazenar dados da empresa
  const [dataEmpresa] = useState<string | null>(
    localStorage.getItem("dataEmpresa")
  );

  // Estados para projetos e times
  const [times, setTimes] = useState<any[]>([]);
  const [projetos1, setProjetos1] = useState<any[]>([]);
  const [projetos2, setProjetos2] = useState<any[]>([]);
  const [projetos3, setProjetos3] = useState<any[]>([]);
  const [projetos4, setProjetos4] = useState<any[]>([]);
  const [projetos5, setProjetos5] = useState<any[]>([]);

  // Estados para empresas e centros de custo
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [listaEmpresas, setListaEmpresas] = useState<Empresa[]>([]);
  const [centrosDeCusto, setCentrosDeCusto] = useState<string[]>([]);
  const [valorCentroCusto, setValorCentroCusto] = useState<CentroCustoValue[]>(
    []
  );
  const [nomesCC, setNomesCC] = useState<string[]>([]);
  const [nomesPJ, setNomesPJ] = useState<string[]>([]);

  // Handlers para datas
  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  // Função para formatar data
  function formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");

    return `${ano}-${mes}-${dia}`;
  }

  // Funções para buscar dados financeiros
  const getTotalSalarios = async (): Promise<number> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return 0;
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallfuncionariosrf/`
      );
      let totalSalarios = 0.0;

      // Ordena a lista por data de entrada
      const listaOrdenada = response.data.sort(
        (a: any, b: any) =>
          new Date(a.dataentrada).getTime() - new Date(b.dataentrada).getTime()
      );

      const dataInicial = new Date(startDate);
      const dataFinal = new Date(endDate);

      // Soma os salários dentro do intervalo de datas
      listaOrdenada.forEach((e: any) => {
        const dataRegistro = new Date(e.dataregistro);
        if (dataRegistro >= dataInicial && dataRegistro <= dataFinal) {
          const salario = parseFloat(e.salario);
          if (!isNaN(salario)) {
            totalSalarios += salario;
          } else {
            console.warn(
              `Salário inválido para registro: ${JSON.stringify(e)}`
            );
          }
        }
      });

      return totalSalarios;
    } catch (error) {
      console.error("Erro ao buscar total de salários:", error);
      return 0;
    }
  };

  const getLucroBruto = async (): Promise<number> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return 0;
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallentradas/`
      );
      let lucroBruto = 0.0;

      // Ordena a lista por data de entrada
      const listaOrdenada = response.data.sort(
        (a: any, b: any) =>
          new Date(a.dataentrada).getTime() - new Date(b.dataentrada).getTime()
      );

      const dataInicial = new Date(startDate);
      const dataFinal = new Date(endDate);

      // Soma os valores das entradas dentro do intervalo de datas
      listaOrdenada.forEach((e: any) => {
        const dataEntrada = new Date(e.dataentrada);
        if (dataEntrada >= dataInicial && dataEntrada <= dataFinal) {
          const valorEntrada = parseFloat(e.valorentrada);
          if (!isNaN(valorEntrada)) {
            lucroBruto += valorEntrada;
          } else {
            console.warn(
              `Valor de entrada inválido para registro: ${JSON.stringify(e)}`
            );
          }
        }
      });

      return lucroBruto;
    } catch (error) {
      console.error("Erro ao buscar lucro bruto:", error);
      return 0;
    }
  };

  const getDespesas = async (): Promise<{
    despesaFixa: number;
    despesaVariavel: number;
  }> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return { despesaFixa: 0, despesaVariavel: 0 };
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallsaidas/`
      );

      let despesaFixa = 0.0;
      let despesaVariavel = 0.0;

      // Ordena a lista por data de saída
      const listaOrdenada = response.data.sort(
        (a: any, b: any) =>
          new Date(a.datasaida).getTime() - new Date(b.datasaida).getTime()
      );

      const dataInicial = new Date(startDate);
      const dataFinal = new Date(endDate);

      // Soma as despesas dentro do intervalo de datas
      listaOrdenada.forEach((e: any) => {
        const dataSaida = new Date(e.datasaida);
        if (dataSaida >= dataInicial && dataSaida <= dataFinal) {
          const valorSaida = parseFloat(e.valorsaida);
          if (!isNaN(valorSaida)) {
            if (e.tipodespesa === "Fixa") {
              despesaFixa += valorSaida;
            } else {
              despesaVariavel += valorSaida;
            }
          } else {
            console.warn(
              `Valor de saída inválido para registro: ${JSON.stringify(e)}`
            );
          }
        }
      });

      return {
        despesaFixa,
        despesaVariavel,
      };
    } catch (error) {
      console.error("Erro ao buscar despesas:", error);
      return { despesaFixa: 0, despesaVariavel: 0 };
    }
  };

  const getLucroLiquido = async (): Promise<number> => {
    try {
      const despesas = await getDespesas();
      const somaDespesas = despesas.despesaFixa + despesas.despesaVariavel;
      const bruto = await getLucroBruto();
      return bruto - somaDespesas;
    } catch (error) {
      console.error("Erro ao calcular lucro líquido:", error);
      return 0;
    }
  };

  const getContasAPagar = async (): Promise<void> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return;
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallsaidas/`
      );

      let contasAPagar = 0.0;
      let contasAReceber = 0.0;

      // Ordena a lista por data de saída
      const listaOrdenada = response.data.sort(
        (a: any, b: any) =>
          new Date(a.datasaida).getTime() - new Date(b.datasaida).getTime()
      );

      const dataInicial = new Date(startDate);
      const dataFinal = new Date(endDate);

      // Soma as contas a pagar e a receber dentro do intervalo de datas
      listaOrdenada.forEach((e: any) => {
        const dataSaida = new Date(e.datasaida);
        if (dataSaida >= dataInicial && dataSaida <= dataFinal) {
          const valorSaida = parseFloat(e.valorsaida);
          if (!isNaN(valorSaida)) {
            if (e.tipoconta === "A Pagar") {
              contasAPagar += valorSaida;
            } else if (e.tipoconta === "A Receber") {
              contasAReceber += valorSaida;
            }
          } else {
            console.warn(
              `Valor de saída inválido para registro: ${JSON.stringify(e)}`
            );
          }
        }
      });

      setAPagar(contasAPagar);
      setAReceber(contasAReceber);
    } catch (error) {
      console.error("Erro ao buscar contas a pagar/receber:", error);
    }
  };

  // Função para obter o nome do mês com offset
  function getMonthName(monthOffset: number): string {
    const months = [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ];
    const date = new Date();
    date.setMonth(date.getMonth() + monthOffset);
    return months[date.getMonth()];
  }

  // Função para obter o mês atual com offset em formato string
  function getMesAtualEmString(n: number): string {
    const dataAtual = new Date();
    // Adiciona n meses ao mês atual
    dataAtual.setMonth(dataAtual.getMonth() + n);

    // Retorna o mês ajustado, com o formato 'MM'
    return (dataAtual.getMonth() + 1).toString().padStart(2, "0");
  }

  // Funções para obter lucro bruto e despesas mensais
  const lucroBrutoMensal = async (mes: string): Promise<number> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return 0;
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallentradasbymonth/${mes}/${anoSelecionadoGrafico}`
      );

      let somaLucroBrutoMensal = 0.0;

      if (Array.isArray(response.data)) {
        response.data.forEach((e: any) => {
          const valorEntrada = parseFloat(e.valorentrada);
          if (!isNaN(valorEntrada)) {
            somaLucroBrutoMensal += valorEntrada;
          } else {
            console.warn(
              `Valor de entrada inválido para registro: ${JSON.stringify(e)}`
            );
          }
        });
      }

      return somaLucroBrutoMensal;
    } catch (error) {
      console.error("Erro ao buscar o lucro bruto mensal:", error);
      return 0;
    }
  };

  const getDespesasMensais = async (
    mes: string
  ): Promise<{ despesaFixa: number; despesaVariavel: number }> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return { despesaFixa: 0, despesaVariavel: 0 };
      }

      const response: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallsaidasbymonth/${mes}/${anoSelecionadoGrafico}`
      );

      let despesaFixa = 0.0;
      let despesaVariavel = 0.0;

      response.data.forEach((saida: any) => {
        const valorSaida = parseFloat(saida.valorsaida);
        if (!isNaN(valorSaida)) {
          if (saida.tipodespesa === "Fixa") {
            despesaFixa += valorSaida;
          } else {
            despesaVariavel += valorSaida;
          }
        } else {
          console.warn(
            `Valor de saída inválido para registro: ${JSON.stringify(saida)}`
          );
        }
      });

      return {
        despesaFixa,
        despesaVariavel,
      };
    } catch (error) {
      console.error("Erro ao buscar as saídas mensais:", error);
      return { despesaFixa: 0, despesaVariavel: 0 };
    }
  };

  // Função para calcular lucro líquido mensal
  const lucroLiquidoMensal = async (mes: string): Promise<number> => {
    const lucroBruto = await lucroBrutoMensal(mes);
    const despesas = await getDespesasMensais(mes);
    return lucroBruto - (despesas.despesaFixa + despesas.despesaVariavel);
  };

  // Estados para lucros e despesas mensais
  const [lucroBruto01, setLucroBruto01] = useState<number>(0);
  const [lucroBruto02, setLucroBruto02] = useState<number>(0);
  const [lucroBruto03, setLucroBruto03] = useState<number>(0);
  const [lucroBruto04, setLucroBruto04] = useState<number>(0);
  const [lucroBruto05, setLucroBruto05] = useState<number>(0);
  const [lucroBruto06, setLucroBruto06] = useState<number>(0);
  const [lucroBruto07, setLucroBruto07] = useState<number>(0);
  const [lucroBruto08, setLucroBruto08] = useState<number>(0);
  const [lucroBruto09, setLucroBruto09] = useState<number>(0);
  const [lucroBruto10, setLucroBruto10] = useState<number>(0);
  const [lucroBruto11, setLucroBruto11] = useState<number>(0);
  const [lucroBruto12, setLucroBruto12] = useState<number>(0);
  const [lucroBruto13, setLucroBruto13] = useState<number>(0);
  const [lucroBruto14, setLucroBruto14] = useState<number>(0);

  const [lucroLiquido01, setLucroLiquido01] = useState<number>(0);
  const [lucroLiquido02, setLucroLiquido02] = useState<number>(0);
  const [lucroLiquido03, setLucroLiquido03] = useState<number>(0);
  const [lucroLiquido04, setLucroLiquido04] = useState<number>(0);
  const [lucroLiquido05, setLucroLiquido05] = useState<number>(0);
  const [lucroLiquido06, setLucroLiquido06] = useState<number>(0);
  const [lucroLiquido07, setLucroLiquido07] = useState<number>(0);
  const [lucroLiquido08, setLucroLiquido08] = useState<number>(0);
  const [lucroLiquido09, setLucroLiquido09] = useState<number>(0);
  const [lucroLiquido10, setLucroLiquido10] = useState<number>(0);
  const [lucroLiquido11, setLucroLiquido11] = useState<number>(0);
  const [lucroLiquido12, setLucroLiquido12] = useState<number>(0);
  const [lucroLiquido13, setLucroLiquido13] = useState<number>(0);
  const [lucroLiquido14, setLucroLiquido14] = useState<number>(0);

  const [despesaFixa01, setDespesaFixa01] = useState<number>(0);
  const [despesaFixa02, setDespesaFixa02] = useState<number>(0);
  const [despesaFixa03, setDespesaFixa03] = useState<number>(0);
  const [despesaFixa04, setDespesaFixa04] = useState<number>(0);
  const [despesaFixa05, setDespesaFixa05] = useState<number>(0);
  const [despesaFixa06, setDespesaFixa06] = useState<number>(0);
  const [despesaFixa07, setDespesaFixa07] = useState<number>(0);
  const [despesaFixa08, setDespesaFixa08] = useState<number>(0);
  const [despesaFixa09, setDespesaFixa09] = useState<number>(0);
  const [despesaFixa10, setDespesaFixa10] = useState<number>(0);
  const [despesaFixa11, setDespesaFixa11] = useState<number>(0);
  const [despesaFixa12, setDespesaFixa12] = useState<number>(0);
  const [despesaFixa13, setDespesaFixa13] = useState<number>(0);
  const [despesaFixa14, setDespesaFixa14] = useState<number>(0);

  const [despesaVariavel01, setDespesaVariavel01] = useState<number>(0);
  const [despesaVariavel02, setDespesaVariavel02] = useState<number>(0);
  const [despesaVariavel03, setDespesaVariavel03] = useState<number>(0);
  const [despesaVariavel04, setDespesaVariavel04] = useState<number>(0);
  const [despesaVariavel05, setDespesaVariavel05] = useState<number>(0);
  const [despesaVariavel06, setDespesaVariavel06] = useState<number>(0);
  const [despesaVariavel07, setDespesaVariavel07] = useState<number>(0);
  const [despesaVariavel08, setDespesaVariavel08] = useState<number>(0);
  const [despesaVariavel09, setDespesaVariavel09] = useState<number>(0);
  const [despesaVariavel10, setDespesaVariavel10] = useState<number>(0);
  const [despesaVariavel11, setDespesaVariavel11] = useState<number>(0);
  const [despesaVariavel12, setDespesaVariavel12] = useState<number>(0);
  const [despesaVariavel13, setDespesaVariavel13] = useState<number>(0);
  const [despesaVariavel14, setDespesaVariavel14] = useState<number>(0);

  const [loading, setLoading] = useState(true);
  const [loadingChart1, setLoadingChart1] = useState(true);
  const [loadingChart2, setLoadingChart2] = useState(true);
  const [loadingChart3, setLoadingChart3] = useState(true);
  const [anoSelecionadoGrafico, setAnoSelecionadoGrafico] =
    useState<string>("2024");

  // Função para buscar lucros e despesas mensais
  useEffect(() => {
    const fetchDados = async () => {
      try {
        // Lista de meses
        const meses = [
          "01",
          "02",
          "03",
          "04",
          "05",
          "06",
          "07",
          "08",
          "09",
          "10",
          "11",
          "12",
        ];

        // Realiza chamadas para lucros e despesas em paralelo
        const [lucrosBrutos, lucrosLiquidos, despesasMensais] =
          await Promise.all([
            Promise.all(meses.map((mes) => lucroBrutoMensal(mes))),
            Promise.all(meses.map((mes) => lucroLiquidoMensal(mes))),
            Promise.all(meses.map((mes) => getDespesasMensais(mes))),
          ]);

        // Formatador de moeda
        const formatCurrency = (valor: number) =>
          new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(valor);

        // Atualiza estados de lucros brutos
        setLucroBruto01(lucrosBrutos[0]);
        setLucroBruto02(lucrosBrutos[1]);
        setLucroBruto03(lucrosBrutos[2]);
        setLucroBruto04(lucrosBrutos[3]);
        setLucroBruto05(lucrosBrutos[4]);
        setLucroBruto06(lucrosBrutos[5]);
        setLucroBruto07(lucrosBrutos[6]);
        setLucroBruto08(lucrosBrutos[7]);
        setLucroBruto09(lucrosBrutos[8]);
        setLucroBruto10(lucrosBrutos[9]);
        setLucroBruto11(lucrosBrutos[10]);
        setLucroBruto12(lucrosBrutos[11]);
        // setLucroBruto13(lucrosBrutos[12]);
        // setLucroBruto14(lucrosBrutos[13]);

        // Atualiza estados de lucros líquidos
        setLucroLiquido01(lucrosLiquidos[0]);
        setLucroLiquido02(lucrosLiquidos[1]);
        setLucroLiquido03(lucrosLiquidos[2]);
        setLucroLiquido04(lucrosLiquidos[3]);
        setLucroLiquido05(lucrosLiquidos[4]);
        setLucroLiquido06(lucrosLiquidos[5]);
        setLucroLiquido07(lucrosLiquidos[6]);
        setLucroLiquido08(lucrosLiquidos[7]);
        setLucroLiquido09(lucrosLiquidos[8]);
        setLucroLiquido10(lucrosLiquidos[9]);
        setLucroLiquido11(lucrosLiquidos[10]);
        setLucroLiquido12(lucrosLiquidos[11]);
        // setLucroLiquido13(lucrosLiquidos[12]);
        // setLucroLiquido14(lucrosLiquidos[13]);

        // Atualiza despesas fixas
        const despesasFixas = despesasMensais.map(
          (despesa) => despesa.despesaFixa
        );
        setDespesaFixa01(despesasFixas[0]);
        setDespesaFixa02(despesasFixas[1]);
        setDespesaFixa03(despesasFixas[2]);
        setDespesaFixa04(despesasFixas[3]);
        setDespesaFixa05(despesasFixas[4]);
        setDespesaFixa06(despesasFixas[5]);
        setDespesaFixa07(despesasFixas[6]);
        setDespesaFixa08(despesasFixas[7]);
        setDespesaFixa09(despesasFixas[8]);
        setDespesaFixa10(despesasFixas[9]);
        setDespesaFixa11(despesasFixas[10]);
        setDespesaFixa12(despesasFixas[11]);
        // setDespesaFixa13(despesasFixas[12]);
        // setDespesaFixa14(despesasFixas[13]);

        // Atualiza despesas variáveis
        const despesasVariaveis = despesasMensais.map(
          (despesa) => despesa.despesaVariavel
        );
        setDespesaVariavel01(despesasVariaveis[0]);
        setDespesaVariavel02(despesasVariaveis[1]);
        setDespesaVariavel03(despesasVariaveis[2]);
        setDespesaVariavel04(despesasVariaveis[3]);
        setDespesaVariavel05(despesasVariaveis[4]);
        setDespesaVariavel06(despesasVariaveis[5]);
        setDespesaVariavel07(despesasVariaveis[6]);
        setDespesaVariavel08(despesasVariaveis[7]);
        setDespesaVariavel09(despesasVariaveis[8]);
        setDespesaVariavel10(despesasVariaveis[9]);
        setDespesaVariavel11(despesasVariaveis[10]);
        setDespesaVariavel12(despesasVariaveis[11]);
        // setDespesaVariavel13(despesasVariaveis[12]);
        // setDespesaVariavel14(despesasVariaveis[13]);
      } catch (error) {
        console.error("Erro ao buscar dados mensais:", error);
      } finally {
        setLoadingChart1(false);
        setLoadingChart2(false);
        setLoadingChart3(false);
      }
    };

    fetchDados();
  }, [anoSelecionadoGrafico]);

  // Dados para os gráficos
  const dataPie: any[] = [
    ["Task", "Amount"],
    ["Contas a Pagar", aPagar],
    ["Contas a Receber", aReceber],
  ];

  const optionsPie = {
    title: "Contas a Pagar e Receber",
  };

  const formatters = [
    {
      type: "NumberFormat" as const,
      column: 1,
      options: {
        prefix: "R$", // Usando o símbolo da moeda brasileira
        negativeColor: "red",
        negativeParens: true,
        locale: "pt-BR", // Ajustando para o formato de número brasileiro
        fractionDigits: 2, // Definindo a quantidade de casas decimais
      },
    },
    {
      type: "NumberFormat" as const,
      column: 2, // Para a segunda coluna (no seu caso "Lucro Líquido" ou "Despesas Fixas")
      options: {
        prefix: "R$", // Usando o símbolo da moeda brasileira
        negativeColor: "red",
        negativeParens: true,
        locale: "pt-BR",
        fractionDigits: 2,
      },
    },
  ];

  const dataLine: any[] = [
    ["Mês", "Receitas"],
    ["Janeiro", lucroBruto01],
    ["Fevereiro", lucroBruto02],
    ["Março", lucroBruto03],
    ["Abril", lucroBruto04],
    ["Maio", lucroBruto05],
    ["Junho", lucroBruto06],
    ["Julho", lucroBruto07],
    ["Agosto", lucroBruto08],
    ["Setembro", lucroBruto09],
    ["Outubro", lucroBruto10],
    ["Novembro", lucroBruto11],
    ["Dezembro", lucroBruto12],
    // [getMonthName(2), lucroBruto13, lucroLiquido13],
    // [getMonthName(3), lucroBruto14, lucroLiquido14],
  ];

  const optionsLine = {
    chart: {
      title: "Receitas Protmar",
      subtitle: "Em milhares de R$",
    },
    hAxis: {
      title: "Mês",
      slantedText: true,
      slantedTextAngle: 45,
    },
    vAxis: {
      title: "Valores (R$)",
      format: "currency",
      locale: "pt-BR",
    },
    tooltip: {
      trigger: "both", // Mostra o tooltip em ambos os eixos
    },
    legend: { position: "bottom" },
  };

  const dataLine2: any[] = [
    [
      "Mês",
      `Despesas
      Variaveis`,
      `Despesas
      Fixas`,
    ],
    ["Janeiro", despesaVariavel01, despesaFixa01],
    ["Fevereiro", despesaVariavel02, despesaFixa02],
    ["Março", despesaVariavel03, despesaFixa03],
    ["Abril", despesaVariavel04, despesaFixa04],
    ["Maio", despesaVariavel05, despesaFixa05],
    ["Junho", despesaVariavel06, despesaFixa06],
    ["Julho", despesaVariavel07, despesaFixa07],
    ["Agosto", despesaVariavel08, despesaFixa08],
    ["Setembro", despesaVariavel09, despesaFixa09],
    ["Outubro", despesaVariavel10, despesaFixa10],
    ["Novembro", despesaVariavel11, despesaFixa11],
    ["Dezembro", despesaVariavel12, despesaFixa12],
    // [getMonthName(2), despesaVariavel13, despesaFixa13],
    // [getMonthName(3), despesaVariavel14, despesaFixa14],
  ];

  const optionsLine2 = {
    chart: {
      title: "Despesas Protmar",
      subtitle: "Em milhares de R$",
    },
    hAxis: {
      title: "Mês",
      slantedText: true,
      slantedTextAngle: 45,
    },
    vAxis: {
      title: "Valores (R$)",
      format: "currency",
      locale: "pt-BR",
    },
    tooltip: {
      trigger: "both",
    },
    legend: { position: "bottom" },
  };

  const dataLine3: any[] = [
    ["Mês", "Lucro"],
    ["Janeiro", lucroLiquido01],
    ["Fevereiro", lucroLiquido02],
    ["Março", lucroLiquido03],
    ["Abril", lucroLiquido04],
    ["Maio", lucroLiquido05],
    ["Junho", lucroLiquido06],
    ["Julho", lucroLiquido07],
    ["Agosto", lucroLiquido08],
    ["Setembro", lucroLiquido09],
    ["Outubro", lucroLiquido10],
    ["Novembro", lucroLiquido11],
    ["Dezembro", lucroLiquido12],
    // [getMonthName(2), lucroBruto13, lucroLiquido13],
    // [getMonthName(3), lucroBruto14, lucroLiquido14],
  ];

  const optionsLine3 = {
    chart: {
      title: "Lucro Protmar",
      subtitle: "Em milhares de R$",
    },
    hAxis: {
      title: "Mês",
      slantedText: true,
      slantedTextAngle: 45,
    },
    vAxis: {
      title: "Valores (R$)",
      format: "currency",
      locale: "pt-BR",
    },
    tooltip: {
      trigger: "both",
    },
    legend: { position: "bottom" },
  };

  const [empresasEntradas, setEmpresasEntradas] = useState<any>("");

  const getListaEmpresasComIds = async (): Promise<void> => {
    try {
      if (!dataEmpresa) {
        console.error("dataEmpresa não está definido no localStorage.");
        return;
      }

      // Obtém os dados de saídas
      const responseSaidas: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallsaidasintervaldata/${startDate}/${endDate}`
      );

      // Obtém os dados de entradas
      const responseEntradas: AxiosResponse<any[]> = await axios.get<any[]>(
        `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallentradasintervaldata/${startDate}/${endDate}`
      );

      // Inicializa o objeto para armazenar os valores acumulados por empresa
      const empresas: Record<
        string,
        { somaEntradas: number; somaSaidas: number }
      > = {};

      // Itera pelos dados de entradas para calcular os totais por empresa
      responseEntradas.data.forEach((item: any) => {
        const { empresaselecionada, valorentrada } = item;
        const valor = parseFloat(valorentrada);

        if (empresaselecionada && !isNaN(valor)) {
          if (!empresas[empresaselecionada]) {
            empresas[empresaselecionada] = { somaEntradas: 0, somaSaidas: 0 };
          }
          empresas[empresaselecionada].somaEntradas += valor;
        }
      });

      // Itera pelos dados de saídas para calcular os totais por empresa
      responseSaidas.data.forEach((item: any) => {
        const { empresaselecionada, valorsaida } = item;
        const valor = parseFloat(valorsaida);

        if (empresaselecionada && !isNaN(valor)) {
          if (!empresas[empresaselecionada]) {
            empresas[empresaselecionada] = { somaEntradas: 0, somaSaidas: 0 };
          }
          empresas[empresaselecionada].somaSaidas += valor;
        }
      });

      // Converte o objeto em uma lista para armazenar no estado
      const listaEmpresas = Object.entries(empresas).map(
        ([empresa, valores]) => ({
          nomeEmpresa: empresa,
          somaEntradas: valores.somaEntradas,
          somaSaidas: valores.somaSaidas,
        })
      );

      // Atualiza o estado com os valores formatados
      setEmpresasEntradas(listaEmpresas);
    } catch (error) {
      console.error("Erro ao agrupar empresas:", error);
    }
  };

  const [loadInfoEspecificaByEmpresa, setLoadInfoEspecificaByEmpresa] =
    useState(true);

  useEffect(() => {
    try {
      setLoadInfoEspecificaByEmpresa(true);
      getListaEmpresasComIds();
      fetchRetornoCC();
    } catch (error) {
    } finally {
      setLoadInfoEspecificaByEmpresa(false);
    }
  }, [startDate, endDate, dataEmpresa]);

  // Função para somar valores das empresas
  const getSomaEmpresa = (
    empresa: string
  ): { somaTotal: number; somaTotalEntrada: number } => {
    let somaTotal = 0;
    let somaTotalEntrada = 0;

    listaEmpresas.forEach((e: Empresa) => {
      if (empresa === e.empresaselecionada) {
        somaTotal += parseFloat(e.valorsaida.toString());
        somaTotalEntrada += parseFloat(e.valorentrada.toString());
      }
    });

    return {
      somaTotal,
      somaTotalEntrada,
    };
  };

  // Hook para buscar e definir a lista de empresas ao montar o componente

  // Função para buscar centros de custo únicos
  useEffect(() => {
    const fetchCentrosDeCusto = async () => {
      try {
        if (!dataEmpresa) {
          console.error("dataEmpresa não está definido no localStorage.");
          return;
        }

        const response: AxiosResponse<Saida[]> = await axios.get<Saida[]>(
          `https://financeiro.api.protmar.com.br/${dataEmpresa}/getallsaidas/`
        );

        const centrosDeCustoUnicos: string[] = Array.from(
          new Set(response.data.map((item: Saida) => item.centrocusto))
        );

        const projetosUnicos: string[] = Array.from(
          new Set(response.data.flatMap((item: Saida) => item.projetos))
        );

        setNomesPJ(projetosUnicos);
        setNomesCC(centrosDeCustoUnicos);
      } catch (error) {
        console.error("Erro ao buscar os centros de custo:", error);
      }
    };

    fetchCentrosDeCusto();
  }, []);

  const handleBuscarClick = async () => {
    try {
      const bruto = await getLucroBruto();
      setLucroBruto(Number(bruto.toFixed(2)));

      const despesas = await getDespesas();
      setDespesasFixas(Number(despesas.despesaFixa.toFixed(2)));
      setDespesasVariadas(Number(despesas.despesaVariavel.toFixed(2)));

      const liquido = await getLucroLiquido();
      setLucroLiquido(Number(liquido.toFixed(2)));

      const salarios = await getTotalSalarios();
      setTotalSalarios(Number(salarios.toFixed(2)));

      await getContasAPagar();
    } catch (error) {
      console.error("Erro ao buscar dados ao clicar em Buscar:", error);
    }
  };

  const [valoresCentroCusto, setValoresCentroCusto] = useState<any[]>([]);

  const [valoresProjetos, setValoresProjetos] = useState<any[]>([]);

  const fetchRetornoCC = async () => {
    try {
      // Verifica se 'nomesCC' e 'nomesPJ' estão populados
      if (nomesCC.length === 0 && nomesPJ.length === 0) {
        console.warn(
          "nomesCC e nomesPJ estão vazios. Abortando fetchRetornoCC."
        );
        return;
      }

      // Verifica se as datas estão definidas
      if (!startDate || !endDate) {
        console.error("Datas de início e fim não estão definidas.");
        return;
      }

      // Limpa os estados inicialmente
      setValoresCentroCusto([]);
      setValoresProjetos([]);

      // Cria as promessas para centro de custo
      const centroCustoPromises = nomesCC.map(async (e) => {
        if (!e) {
          console.warn("Valor inválido em nomesCC:", e);
          return null;
        }

        try {
          const response = await axios.get(
            `https://financeiro.api.protmar.com.br/${dataEmpresa}/getvaluebycc/${e}/${startDate}/${endDate}`
          );

          // Verifica se response.data não é nulo
          if (!response.data) {
            console.warn(`Nenhum dado retornado para centro de custo: ${e}`);
            return null;
          }

          return {
            centrocusto: response.data.centrocusto,
            totalValorSaida: response.data.totalValorSaida,
          };
        } catch (error) {
          console.error(
            `Erro ao buscar dados para centro de custo ${e}:`,
            error
          );
          return null;
        }
      });

      // Espera todas as promessas serem resolvidas
      const centroCustoResultados = await Promise.all(centroCustoPromises);

      // Filtra valores nulos ou indefinidos
      const filteredCentroCustoResultados = centroCustoResultados.filter(
        (item) => item !== null && item !== undefined
      );

      // Atualiza o estado com os resultados filtrados
      setValoresCentroCusto(filteredCentroCustoResultados);

      // Repete o mesmo para projetos
      const projetoPromises = nomesPJ.map(async (e) => {
        if (!e) {
          console.warn("Valor inválido em nomesPJ:", e);
          return null;
        }

        try {
          const response = await axios.get(
            `https://financeiro.api.protmar.com.br/${dataEmpresa}/getvaluebypj/${e}/${startDate}/${endDate}`
          );

          if (!response.data) {
            console.warn(`Nenhum dado retornado para projeto: ${e}`);
            return null;
          }

          return {
            projeto: response.data.projetos,
            totalValorSaida: response.data.totalValorSaida,
          };
        } catch (error) {
          console.error(`Erro ao buscar dados para projeto ${e}:`, error);
          return null;
        }
      });

      const projetoResultados = await Promise.all(projetoPromises);

      const filteredProjetoResultados = projetoResultados.filter(
        (item) => item !== null && item !== undefined
      );

      setValoresProjetos(filteredProjetoResultados);
    } catch (error) {
      console.error("Erro ao buscar os dados:", error);
    }
  };

  return (
    <main id="ILRGMain">
      <div id="ILRGContainer03">
        <div>
          <label htmlFor="start" className="ILRGTituloInput">
            Data inicial:
          </label>
          <input
            type="date"
            id="start"
            name="start"
            value={startDate}
            onChange={handleStartDateChange}
          />

          <label htmlFor="end" className="ILRGTituloInput">
            Data final:
          </label>
          <input
            type="date"
            id="end"
            name="end"
            value={endDate}
            onChange={handleEndDateChange}
            min={startDate}
          />
          <button
            className="btn btn-primary"
            onClick={handleBuscarClick}
            id="ILRGTituloButton"
          >
            Buscar
          </button>
        </div>
      </div>

      <div id="ILRGContainerGeral">
        <nav id="grafico01">
          {/* Tabela de Resumo Financeiro */}
          <table
            className="table"
            style={{ width: "100%", tableLayout: "fixed", marginTop: "10px" }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid green",
                    margin: "5px",
                    maxWidth: "200px",
                  }}
                >
                  <h6>Receitas</h6>
                  <h3>
                    {lucroBruto.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </h3>
                </td>

                <td
                  style={{
                    border: "1px solid green",
                    margin: "5px",
                    maxWidth: "200px",
                  }}
                >
                  <h6>Despesas Fixas</h6>
                  <h3>
                    {despesasFixas.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </h3>
                </td>
                <td
                  style={{
                    border: "1px solid green",
                    margin: "5px",
                    maxWidth: "200px",
                  }}
                >
                  <h6>Despesas Variaveis</h6>
                  <h3>
                    {despesasVariadas.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </h3>
                </td>
                <td
                  style={{
                    border: "1px solid green",
                    margin: "5px",
                    maxWidth: "200px",
                  }}
                >
                  <h6>Total Despesas</h6>
                  <h3>
                    {(despesasVariadas + despesasFixas).toLocaleString(
                      "pt-BR",
                      { style: "currency", currency: "BRL" }
                    )}
                  </h3>
                </td>

                <td
                  style={{
                    border: "1px solid green",
                    margin: "5px",
                    maxWidth: "230px",
                  }}
                >
                  <h6>Lucro</h6> {/* Lucro Líquido */}
                  <h3>
                    {lucroLiquido.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </h3>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Tabela de Accordions */}
          <table
            className="table"
            style={{ width: "100%", tableLayout: "fixed" }}
          >
            <tbody>
              <tr
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <td
                  style={{
                    margin: "5px",
                    width: "100%", // Ajuste de largura total
                    maxWidth: "333px",
                    textAlign: "center", // Centralizar texto (opcional)
                  }}
                >
                  <Accordion flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>Entradas Empresas</Accordion.Header>
                      <Accordion.Body>
                        {loadInfoEspecificaByEmpresa ? (
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : empresasEntradas.length > 0 ? (
                          empresasEntradas.map((empresa: any) => (
                            <div key={empresa.nomeEmpresa}>
                              <strong>{empresa.nomeEmpresa}</strong>
                              <br />
                              <span>
                                Entradas Totais:{" "}
                                {empresa.somaEntradas.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p>Defina uma data inicial e final...</p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </td>

                <td
                  style={{
                    margin: "5px",
                    width: "100%", // Ajuste de largura total
                    maxWidth: "333px",
                    textAlign: "center", // Centralizar texto (opcional)
                  }}
                >
                  <Accordion flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>Saidas Empresas</Accordion.Header>
                      <Accordion.Body>
                        {empresasEntradas.length > 0 ? (
                          empresasEntradas.map((empresa: any) => (
                            <div key={empresa.nomeEmpresa}>
                              <strong>{empresa.nomeEmpresa}</strong>
                              <br />
                              <span>
                                Saidas Totais:{" "}
                                {empresa.somaSaidas.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p>Defina uma data inicial e final...</p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </td>

                {/* Accordion de Gastos Centros de Custos */}
                <td
                  style={{
                    margin: "5px",
                    width: "100%",
                    maxWidth: "333px",
                    textAlign: "center",
                  }}
                >
                  <Accordion flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header
                        onClick={() =>
                          !valoresCentroCusto.length && fetchRetornoCC()
                        }
                      >
                        Gastos Centros de Custos
                      </Accordion.Header>
                      <Accordion.Body>
                        {valoresCentroCusto.length > 0 ? (
                          valoresCentroCusto.map((item) => (
                            <div key={item.id || item.centrocusto}>
                              <strong>{item.centrocusto}</strong>
                              <br />
                              Custos:{" "}
                              {Number(item.totalValorSaida).toLocaleString(
                                "pt-BR",
                                {
                                  style: "currency",
                                  currency: "BRL",
                                }
                              )}
                            </div>
                          ))
                        ) : (
                          <span>Defina uma data inicial e final...</span>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </td>

                {/* Placeholder para Gastos Projetos */}
                <td
                  style={{
                    margin: "5px",
                    width: "100%",
                    maxWidth: "333px",
                    textAlign: "center",
                  }}
                >
                  <Accordion flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header onClick={fetchRetornoCC}>
                        Gastos Projetos
                      </Accordion.Header>
                      <Accordion.Body>
                        {valoresProjetos.length > 0
                          ? valoresProjetos.map((item, index) => (
                              <div key={index}>
                                <strong>{item.projeto}</strong>
                                <br />
                                Custos:{" "}
                                {Number(item.totalValorSaida).toLocaleString(
                                  "pt-BR",
                                  {
                                    style: "currency",
                                    currency: "BRL",
                                  }
                                )}
                              </div>
                            ))
                          : "Defina uma data inicial e final..."}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </td>
              </tr>
            </tbody>
          </table>
        </nav>

        <section className="d-flex flex-column justify-content-center align-items-center w-100">
          <div
            className="mb-3 d-flex justify-content-center align-items-center"
            style={{ width: "100" }}
          >
            <label
              htmlFor="ano"
              className="form-label"
              style={{ marginRight: "10px" }}
            >
              Ano Referente aos Gráficos:
            </label>
            <input
              type="number"
              id="ano"
              name="ano"
              min="1900"
              max="2099"
              step="1"
              defaultValue="2024"
              required
              onChange={(e) => {
                setAnoSelecionadoGrafico(e.target.value);
                setLoadingChart1(true);
                setLoadingChart2(true);
                setLoadingChart3(true);
              }}
              className="form-control"
            />
          </div>

          {/* Gráfico 2 com estado de carregamento */}
          <div
            id="ILRGContainer02"
            className="w-100 d-flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            {loadingChart2 ? (
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading Gráfico 2...</span>
              </div>
            ) : (
              <div className="" style={{ width: "100%" }}>
                <Chart
                  chartType="Line"
                  width="100%"
                  height="300px"
                  data={dataLine}
                  options={optionsLine}
                />

                <table
                  className="table table-bordered table-striped"
                  style={{ width: "100%", marginRight: "30px", scale: "0.7",
                    transform: "translateX(-300px)" }}
                >
                  <thead>
                    <tr>
                      <th>Mês</th>
                      <th>Janeiro</th>
                      <th>Fevereiro</th>
                      <th>Março</th>
                      <th>Abril</th>
                      <th>Maio</th>
                      <th>Junho</th>
                      <th>Julho</th>
                      <th>Agosto</th>
                      <th>Setembro</th>
                      <th>Outubro</th>
                      <th>Novembro</th>
                      <th>Dezembro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Valor</td>
                      <td>
                        {lucroBruto01.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto02.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto03.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto04.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto05.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto06.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto07.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto08.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto09.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto10.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto11.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {lucroBruto12.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <br />
          <br />
          <br />
          <br />
          {/* Gráfico 1 com estado de carregamento */}
          <div
            id="ILRGContainer03"
            className="w-100 mb-4 d-flex justify-content-center align-items-center"
            style={{ height: "400px" }}
          >
            {loadingChart1 ? (
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading Gráfico 1...</span>
              </div>
            ) : (
              <div className="" style={{ width: "100%" }}>
                <Chart
                  chartType="Line"
                  width="100%"
                  height="300px"
                  data={dataLine2}
                  options={optionsLine2}
                />

                <table
                  className="table table-bordered table-striped"
                  style={{
                    width: "100%",
                    scale: "0.7",
                    transform: "translateX(-300px)",
                  }}
                >
                  <thead>
                    <tr>
                      <td></td>
                      <th>Janeiro</th>
                      <th>Fevereiro</th>
                      <th>Março</th>
                      <th>Abril</th>
                      <th>Maio</th>
                      <th>Junho</th>
                      <th>Julho</th>
                      <th>Agosto</th>
                      <th>Setembro</th>
                      <th>Outubro</th>
                      <th>Novembro</th>
                      <th>Dezembro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Despesas Variáveis</td>
                      <td>
                        {despesaVariavel01.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel02.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel03.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel04.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel05.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel06.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel07.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel08.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel09.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel10.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel11.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaVariavel12.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Despesas Fixas</td>
                      <td>
                        {despesaFixa01.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa02.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa03.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa04.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa05.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa06.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa07.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa08.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa09.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa10.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa11.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {despesaFixa12.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td>Soma de Despesas</td>
                      <td>
                        {(despesaFixa01 + despesaVariavel01).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa02 + despesaVariavel02).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa03 + despesaVariavel03).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa04 + despesaVariavel04).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa05 + despesaVariavel05).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa06 + despesaVariavel06).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa07 + despesaVariavel07).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa08 + despesaVariavel08).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa09 + despesaVariavel09).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa10 + despesaVariavel10).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa11 + despesaVariavel11).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                      <td>
                        {(despesaFixa12 + despesaVariavel12).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          }
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <br />
          <br />
          <br />

          <div
            id="ILRGContainer02"
            className="w-100 d-flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            {loadingChart3 ? (
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading Gráfico 3...</span>
              </div>
            ) : (
              <div className="" style={{ width: "100%" }}>
                

                <Chart
                  chartType="Line"
                  width="100%"
                  height="300px"
                  data={dataLine3}
                  options={optionsLine3}
                />

<table
  className="table table-bordered table-striped"
  style={{ width: "100%", marginRight: "30px",scale: "0.7",
    transform: "translateX(-300px)" }}
>
  <thead>
    <tr>
      <th>Mês</th>
      <th>Janeiro</th>
      <th>Fevereiro</th>
      <th>Março</th>
      <th>Abril</th>
      <th>Maio</th>
      <th>Junho</th>
      <th>Julho</th>
      <th>Agosto</th>
      <th>Setembro</th>
      <th>Outubro</th>
      <th>Novembro</th>
      <th>Dezembro</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Valor</td>
      <td>
        {lucroLiquido01.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido02.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido03.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido04.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido05.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido06.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido07.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido08.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido09.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido10.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido11.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
      <td>
        {lucroLiquido12.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}
      </td>
    </tr>
  </tbody>
</table>

              </div>

              
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default InfoLateralResumoGeralComponent;
